import { isValidElement, ReactNode, Ref } from 'react'

import { BodyVariant, SizeOptions } from '../../types/types'
import { AstraParagraph } from '../Typography/AstraParagraph/AstraParagraph'
import { astraTwMerge } from '../Utilities/astraTwMerge'
import { isLightColor } from '../Utilities/colorHelpers'

type Variant = 'filled' | 'outlined'
export type BadgeColor = 'default' | 'info' | 'success' | 'caution' | 'critical' | 'magic' | 'brand' | (string & {})

type Props = {
    /**
     * Size of the badge
     */
    size?: SizeOptions
    /**
     * Text to display in the badge
     */
    text?: string | number
    /**
     * Icon to display in the badge
     */
    icon?: ReactNode
    /**
     * Variant of the badge, either filled or outlined
     */
    variant?: Variant
    /**
     * Color of the badge. Can be a predefined color (default, info, success, caution, critical, magic, brand), a hex value (e.g., '#FF5733'), or an RGB/RGBA value (e.g., 'rgb(255, 87, 51)' or 'rgba(255, 87, 51, 0.8)')
     */
    color?: BadgeColor
    /**
     * If true, the badge takes full width
     */
    fullWidth?: boolean
    /**
     * Accessible label for screen readers. Defaults to `text`; provide it explicitly
     * for icon-only badges (no `text`) so their meaning is not conveyed by the icon alone.
     */
    'aria-label'?: string
    /**
     * Optional Data test id, used for testing
     */
    'data-test'?: string
    /**
     * Ref to the div element
     */
    ref?: Ref<HTMLDivElement>
}

const badgeBaseStyles = 'flex select-none items-center gap-1.5'

const filledColorStyles: Record<BadgeColor, string> = {
    default: 'bg-neutral-rest text-default [&>svg]:text-default',
    success: 'bg-success-subtle-rest text-success [&>svg]:text-success-emphasis',
    info: 'bg-brand-subtle-rest text-info [&>svg]:text-info-emphasis',
    caution: 'bg-caution-subtle-rest text-caution [&>svg]:text-caution-emphasis',
    critical: 'bg-critical-subtle-rest text-critical [&>svg]:text-critical-emphasis',
    magic: 'bg-magic-subtle-rest text-magic [&>svg]:text-magic-emphasis',
    brand: 'bg-brand-subtle-rest text-brand [&>svg]:text-inverted',
}

const outlinedColorStyles: Record<BadgeColor, string> = {
    default: 'border border-strong text-default bg-surface-base',
    success: 'border border-success-base text-success bg-surface-base',
    info: 'border border-info-base text-info bg-surface-base',
    caution: 'border border-caution-base text-caution bg-surface-base',
    critical: 'border border-critical-base text-critical bg-surface-base',
    magic: 'border border-magic-base text-magic bg-surface-base',
    brand: 'border border-brand-base text-brand bg-surface-base',
}

const colorStylesByVariant: Record<Variant, Record<BadgeColor, string>> = {
    filled: filledColorStyles,
    outlined: outlinedColorStyles,
}

const sizeStyles: Record<SizeOptions, string> = {
    small: 'h-5 min-w-5',
    medium: 'h-6 min-w-6',
    large: 'h-7 min-w-7',
}

const bodyVariant: Record<SizeOptions, BodyVariant> = {
    small: 'body-small',
    medium: 'body',
    large: 'body',
}

const iconSizeStyles: Record<SizeOptions, string> = {
    small: '[&>svg]:size-3',
    medium: '[&>svg]:size-4',
    large: '[&>svg]:size-4',
}

const getIconDisplayName = (icon: ReactNode): string | undefined => {
    if (!isValidElement(icon) || typeof icon.type === 'string') return undefined
    const type = icon.type as { displayName?: string; name?: string }
    return type.displayName ?? type.name
}

/**
 *
 * Badges are visual indicator and non-interactive component to either call attention to a related object, or for communicating non-actionable information.
 */
export const AstraBadge = ({
    ref,
    size = 'medium',
    text,
    icon,
    variant = 'filled',
    color = 'default',
    fullWidth = false,
    'aria-label': ariaLabel,
    'data-test': dataTest,
    ...props
}: Props) => {
    // Check if color is a custom value (hex or RGB/RGBA)
    const isCustomColor = color.startsWith('#') || color.startsWith('rgb')
    const predefinedColor = isCustomColor ? 'default' : (color as keyof typeof filledColorStyles)

    // Determine text color based on background luminance for custom colors
    const textColorClass = isCustomColor && variant === 'filled' ? (isLightColor(color) ? 'text-default' : 'text-inverted') : ''

    // Generate custom styles for custom colors (hex or RGB/RGBA)
    const customStyles = isCustomColor
        ? variant === 'filled'
            ? { backgroundColor: color }
            : { borderColor: color, color: color, backgroundColor: 'transparent' }
        : {}

    // Build className parts
    const textStyles = text !== undefined ? 'w-fit rounded-full px-2' : 'justify-center rounded-full'
    const colorStyles = !isCustomColor ? colorStylesByVariant[variant][predefinedColor] : variant === 'outlined' ? 'border' : ''

    return (
        <div
            ref={ref}
            data-test={dataTest}
            aria-label={ariaLabel ?? text?.toString() ?? getIconDisplayName(icon)}
            {...props}
            style={customStyles}
            className={astraTwMerge(
                badgeBaseStyles,
                textStyles,
                sizeStyles[size],
                colorStyles,
                textColorClass,
                iconSizeStyles[size],
                fullWidth ? 'w-full' : 'max-w-full'
            )}
        >
            {icon}
            {text !== undefined && (
                <AstraParagraph variant={bodyVariant[size]} weight="medium" className="truncate text-nowrap text-inherit">
                    {text}
                </AstraParagraph>
            )}
        </div>
    )
}
